import {
  AddWorkflowEdgeRequest,
  AiOutboundPhoneCallNodeData,
  AnalyzerNodeData,
  DataFlaggerNodeData,
  DataIngestionNodeData,
  EmailNotificationNodeData,
  ExecutionEndNodeData,
  ExecutionStartNodeData,
  IfElseNodeData,
  SendSMSNodeData,
  SftpDownloadNodeData,
  SftpUploadNodeData,
  UpdateWorkflowRequest,
  WorkflowEdge,
  WorkflowNode,
  WorkflowNodeType,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  $selectedNode,
  closeNodeConfigurationDrawer,
  openNodeConfigurationDrawer as openNodeConfigurationDrawerStore,
} from '@/stores/workflowStore';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Cog,
  Loader2,
  PauseCircle,
  Play,
  PlayCircle,
  PlaySquare,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import CustomEdge from './components/Edges/CustomEdge';
import NodeConfigurationDrawer from './components/NodeConfigurationDrawer';
import AiOutboundPhoneCallNode from './components/Nodes/AiOutboundPhoneCallNode';
import DataFlaggerNode from './components/Nodes/DataFlaggerNode';
import DataIngestionNode from './components/Nodes/DataIngestionNode';
import EmailNode from './components/Nodes/EmailNode';
import IfElseNode from './components/Nodes/IfElseNode';
import ProcessorNode from './components/Nodes/ProcessorNode';
import SendSMSNode from './components/Nodes/SendSMSNode';
import SFTPDownloadNode from './components/Nodes/SFTPDownloadNode';
import SFTPUploadNode from './components/Nodes/SFTPUploadNode';
import StartNode from './components/Nodes/StartNode';
import StopNode from './components/Nodes/StopNode';
import TimerNode from './components/Nodes/TimerNode';
import { emitEventNodeConfigurationEventListener } from './components/utils/nodeConfigurationUtils';
import WorkflowCancelDrawer from './components/WorkflowCancelDrawer';
import WorkflowGlobalConfigurationDrawer from './components/WorkflowGlobalConfigurationDrawer';
import WorkflowManualRunDrawer from './components/WorkflowManualRunDrawer';
import WorkflowPauseDrawer from './components/WorkflowPauseDrawer';
import WorkflowResumeDrawer from './components/WorkflowResumeDrawer';
import WorkflowSkipDrawer from './components/WorkflowSkipDrawer';
import WorkflowStorage from './components/WorkflowStorage';

export interface FormattedNode {
  id: string;
  type: WorkflowNodeType;
  position: { x: number; y: number };
  data: ExtendedWorkflowNodeData;
}

type ExtendedWorkflowNodeData = (
  | SftpDownloadNodeData
  | SftpUploadNodeData
  | ExecutionStartNodeData
  | ExecutionEndNodeData
  | DataFlaggerNodeData
  | AnalyzerNodeData
  | EmailNotificationNodeData
  | DataIngestionNodeData
  | SendSMSNodeData
  | AiOutboundPhoneCallNodeData
  | IfElseNodeData
) & {
  name?: string;
  order?: number | null;
};

export interface FormattedNode {
  id: string;
  type: WorkflowNodeType;
  position: { x: number; y: number };
  data: ExtendedWorkflowNodeData;
}

export type CreateFlowPageProps = {
  workflowId: string;
};

type DataIngestionNodeConfigurationData = {
  source: string;
} | null;

export const Workflow = ({ workflowId }: CreateFlowPageProps) => {
  const org = useStore($org);

  const ws = useStore($workspace);
  const queryClient = useQueryClient();
  const addingNode = useStore($addingNode);
  const deletingNode = useStore($deletingNode);
  const deletingEdge = useStore($deletingEdge);
  const savingNodeConfiguration = useStore($savingNodeConfiguration);

  // get workflows query
  const workflow = useQuery({
    queryKey: ['getWorkflow', workflowId],
    enabled: !!workflowId,
    queryFn: async () => {
      const res = await WorkflowsService.getWorkflow(workflowId ?? '');
      return res;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
  const isWorkflowPublished = !!workflow?.data?.isActive;

  const [nodes, setNodes] = useNodesState<any>([]);
  const [edges, setEdges] = useEdgesState<any>([]);
  // const { x, y, zoom } = useViewport();
  const { setViewport, zoomIn, zoomOut, getViewport, fitView } = useReactFlow();

  const [workflowMetaData, setWorkflowMetaData] = useState<any>({});

  const [openNodeConfigurationDrawer, setOpenNodeConfigurationDrawer] =
    useState(false);
  const [
    openWorkflowGlobalConfigurationDrawer,
    setOpenWorkflowGlobalConfigurationDrawer,
  ] = useState(false);

  const [openWorkflowStorageDrawer, setOpenWorkflowStorageDrawer] =
    useState(false);

  const [openWorkflowManualRunDrawer, setOpenWorkflowManualRunDrawer] =
    useState(false);

  const [openWorkflowPauseDrawer, setOpenWorkflowPauseDrawer] = useState(false);

  const [openWorkflowResumeDrawer, setOpenWorkflowResumeDrawer] =
    useState(false);

  const [openWorkflowSkipDrawer, setOpenWorkflowSkipDrawer] = useState(false);

  const [openWorkflowCancelDrawer, setOpenWorkflowCancelDrawer] =
    useState(false);

  const [
    dataIngestionNodeConfigurationData,
    setDataIngestionNodeConfigurationData,
  ] = useState<DataIngestionNodeConfigurationData>(null);

  const addEdgeMutation = useMutation({
    mutationFn: async (payload: AddWorkflowEdgeRequest) => {
      // call the api to create a new workspace
      if (workflowId) {
        const res = await WorkflowsService.addWorkflowEdge(workflowId, payload);
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflowId],
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  const unpublishBulkMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      if (workflowId) {
        const res = await WorkflowsService.unpublishBulkWorkflow(workflowId);
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflowId],
      });
    },
    onError: error => {
      console.error(error);
      // Check if error status is 409 (Conflict) to customize the toast message
      if ((error as any).status === 409) {
        toast('Bulk workflow is running, Try again later', {
          duration: 5000,
        });
      } else {
        toast('An error occurred :: Unable to un-publish bulk workflow.', {
          description: error.message,
          duration: 5000,
        });
      }
    },
  });

  const publishWorkflowMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      if (workflowId) {
        const res = await WorkflowsService.publishWorkflow(workflowId);
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflowId],
      });
    },
    onError: error => {
      console.error(error);
      // Check if error status is 409 (Conflict) to customize the toast message
      if ((error as any).status === 409) {
        toast('Bulk workflow is running, Try again later', {
          duration: 5000,
        });
      } else {
        toast('An error occurred :: Unable to un-publish bulk workflow.', {
          description: error.message,
          duration: 5000,
        });
      }
    },
  });

  // currentlyClickedNode has the following structure
  //   {
  //     "id": "i0uPFvQC39En",
  //     "type": "EXECUTION_END",
  //     "position": {
  //         "x": 0,
  //         "y": 100
  //     },
  //     "measured": {
  //         "width": 200,
  //         "height": 43
  //     },
  //     "selected": true,
  //     "data": undefined | any data // this is the data that we need to update
  // }
  const [currentlyClickedNode, setCurrentlyClickedNode] = useState<any>(null);

  //  **** Node types ****
  // EMAIL_NOTIFICATION,
  // PII_REDACTION,
  // ANALYZER,
  // DATA_INGESTION,
  // EXECUTION_START,
  // EXECUTION_END
  // **** Node types ****

  const nodeTypes = useMemo(
    () => ({
      DATA_INGESTION: (props: any) => (
        <DataIngestionNode
          {...props}
          setNodes={setNodes}
          setConfiguration={setDataIngestionNodeConfigurationData}
          configuration={dataIngestionNodeConfigurationData}
          workflowId={workflowId}
          nodeName={props?.data?.name ?? 'Data Ingestion'}
        />
      ),
      EMAIL_NOTIFICATION: (props: any) => (
        <EmailNode
          {...props}
          setNodes={setNodes}
          workflowId={workflowId}
          nodeName={props?.data?.name ?? 'Email SMTP'}
        />
      ),
      IF_ELSE_CONDITION: (props: any) => (
        <IfElseNode {...props} setNodes={setNodes} workflowId={workflowId} />
      ),
      ANALYZER: (props: any) => (
        <ProcessorNode {...props} setNodes={setNodes} workflowId={workflowId} />
      ),
      EXECUTION_START: (props: any) => (
        <StartNode {...props} setNodes={setNodes} workflowId={workflowId} />
      ),
      EXECUTION_END: (props: any) => (
        <StopNode {...props} workflowId={workflowId} />
      ),
      SFTP_UPLOAD: (props: any) => (
        <SFTPUploadNode
          {...props}
          setNodes={setNodes}
          workflowId={workflowId}
        />
      ),
      SFTP_DOWNLOAD: (props: any) => (
        <SFTPDownloadNode
          {...props}
          setNodes={setNodes}
          workflowId={workflowId}
        />
      ),
      DATA_FLAGGER: (props: any) => (
        <DataFlaggerNode
          {...props}
          setNodes={setNodes}
          workflowId={workflowId}
        />
      ),
      TIMER: (props: any) => (
        <TimerNode {...props} setNodes={setNodes} workflowId={workflowId} />
      ),
      SEND_SMS: (props: any) => (
        <SendSMSNode {...props} setNodes={setNodes} workflowId={workflowId} />
      ),
      AI_OUTBOUND_PHONE_CALL: (props: any) => (
        <AiOutboundPhoneCallNode
          {...props}
          setNodes={setNodes}
          workflowId={workflowId}
        />
      ),
    }),
    [setNodes, workflowId, dataIngestionNodeConfigurationData],
  );

  const edgeTypes = useMemo(
    () => ({
      CUSTOM_EDGE: (props: any) => (
        <CustomEdge {...props} workflowId={workflowId} />
      ),
    }),
    [workflowId],
  );

  const onNodeClick = (event: any, node: any) => {
    if (node?.type !== 'EXECUTION_START' && node?.type !== 'EXECUTION_END') {
      console.log('Node Clicked:', node?.id, node?.name);
      closeNodeConfigurationDrawer();
      openNodeConfigurationDrawerStore(node);
    }
  };

  const onPaneClick = () => {
    emitEventNodeConfigurationEventListener('confirmSaveChanges', {}); // this sends the event to the node open configuration drawer to save the changes
    closeNodeConfigurationDrawer();
  };

  const onNodesChange = useCallback(
    (changes: any) => {
      return setNodes(nds => applyNodeChanges(changes, nds));
    },
    [setNodes],
  );

  const onEdgesChange = useCallback(
    (changes: any) =>
      setEdges((eds: any) => {
        return applyEdgeChanges(changes, eds);
      }),
    [setEdges],
  );

  // Adds a new connection here
  const onConnect = useCallback(
    async (params: any) => {
      await addEdgeMutation.mutateAsync({
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
      });
    },
    [addEdgeMutation],
  );

  // mutation to update a node
  const dryRunMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      // call the api to create a new workspace
      if (workflowId) {
        const res = await WorkflowsService.dryRunWorkflow(workflowId);
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflowId],
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast('An error occurred :: Unable to dry run workflow.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // mutation to update a node
  const updateWorkflowMutation = useMutation({
    mutationFn: async ({
      workflowId,
      payload,
    }: {
      workflowId: string;
      payload: UpdateWorkflowRequest;
    }) => {
      // call the api to create a new workspace
      if (workflowId) {
        const res = await WorkflowsService.updateWorkflow(workflowId, payload);
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflowId],
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast('An error occurred :: Unable to dry run workflow.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  useEffect(() => {
    if (workflow?.isSuccess) {
      //console.log('setting nodes');
      if (workflow?.data) {
        $workflow.set(workflow?.data);
        // set the initial nodes
        //console.log('setting nodes');
        if (workflow?.data?.nodes) {
          // first sort the nodes according to the order in ascending order
          workflow?.data?.nodes.sort((a: WorkflowNode, b: WorkflowNode) => {
            return a?.order! - b?.order!;
          });
          // prepare the nodes to set in the local state
          const formattedNodes: FormattedNode[] = workflow?.data?.nodes.map(
            (node: WorkflowNode): FormattedNode => {
              return {
                id: node.id,
                type: node?.type,
                position: { x: node.position.x, y: node.position.y },
                // FIX: include the node name from the workflow data so it persists
                data: {
                  ...node?.data,
                  name: node?.name,
                  order: node?.order || null,
                },
              };
            },
          );

          setNodes(formattedNodes);
        }

        if (workflow?.data?.edges) {
          const formattedEdges = workflow?.data?.edges.map(
            (edge: WorkflowEdge) => {
              return {
                id: edge.id,
                type: 'CUSTOM_EDGE',
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle,
              };
            },
          );
          setEdges(formattedEdges);
        }
      }
    }
  }, [workflow?.isLoading, workflow?.isRefetching, setNodes, setEdges]);

  useEffect(() => {
    $sideBarCollapsed.set(true);
  }, []);

  // THis code automatically zooms to the node when clicked on the config
  // useEffect(() => {
  //   const handleNodeOutOfView = () => {
  //     const viewport = getViewport();
  //     nodes.forEach(async node => {
  //       // Check if the node is outside the visible area
  //       if (
  //         node?.position?.x < viewport.x ||
  //         node?.position?.y < viewport.y ||
  //         node?.position?.x > viewport.x ||
  //         node?.position?.y > viewport.y
  //       ) {
  //         fitView({ duration: 800 });
  //       }
  //     });
  //   };
  //   handleNodeOutOfView();
  // }, [nodes, getViewport, fitView]);

  if (workflow?.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-[90vh] w-[100%]">
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '5px',
          position: 'relative',
        }}
      >
        <div className="absolute top-0 z-10 flex w-[100%] items-start justify-between space-y-2 bg-gradient-to-b from-white/80 to-transparent pb-4 pl-4 pr-4">
          <div className="flex flex-col items-start">
            <h2 className="text-md font-semibold tracking-tight">
              {workflow?.data?.name?.replace(/\s*workflow\s*$/i, '')}
            </h2>
            <div className="flex items-center text-[11px] text-gray-500">
              <span>Status: </span>
              <span className="ml-1">
                {workflow?.data?.isActive ? 'Published' : 'Unpublished'}
              </span>
              {savingNodeConfiguration && (
                <span className="ml-1">(Saving...)</span>
              )}
            </div>
          </div>
          <div className="flex overflow-x-auto">
            <div className="flex items-center gap-2 rounded-md border bg-white pl-1 pr-1 text-sm">
              <div
                className={`flex items-center rounded-md p-1 pl-2 pr-2  ${
                  !isWorkflowPublished
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:cursor-pointer hover:bg-slate-200'
                }`}
                onClick={() => {
                  if (!isWorkflowPublished) return;
                  setCurrentlyClickedNode(null);
                  setOpenNodeConfigurationDrawer(false);
                  setOpenWorkflowGlobalConfigurationDrawer(false);
                  setOpenWorkflowStorageDrawer(false);
                  setOpenWorkflowManualRunDrawer(false);
                  setOpenWorkflowPauseDrawer(false);
                  setOpenWorkflowResumeDrawer(false);
                  setOpenWorkflowSkipDrawer(false);
                  setOpenWorkflowCancelDrawer(true);
                }}
              >
                <X className="mr-1 h-4 w-4" /> Cancel Run
              </div>
              <Separator orientation="vertical" />
              <div
                className={`flex items-center rounded-md p-1 pl-2 pr-2  ${
                  !isWorkflowPublished
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:cursor-pointer hover:bg-slate-200'
                }`}
                onClick={() => {
                  if (!isWorkflowPublished) return;
                  setCurrentlyClickedNode(null);
                  setOpenNodeConfigurationDrawer(false);
                  setOpenWorkflowGlobalConfigurationDrawer(false);
                  setOpenWorkflowStorageDrawer(false);
                  setOpenWorkflowManualRunDrawer(false);
                  setOpenWorkflowPauseDrawer(false);
                  setOpenWorkflowResumeDrawer(false);
                  setOpenWorkflowSkipDrawer(true);
                  setOpenWorkflowCancelDrawer(false);
                }}
              >
                <PlaySquare className="mr-1 h-4 w-4" /> Skip Timer Node
              </div>
              <Separator orientation="vertical" />
              <div
                className={`flex items-center rounded-md p-1 pl-2 pr-2  ${
                  !isWorkflowPublished
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:cursor-pointer hover:bg-slate-200'
                }`}
                onClick={() => {
                  if (!isWorkflowPublished) return;
                  setCurrentlyClickedNode(null);
                  setOpenNodeConfigurationDrawer(false);
                  setOpenWorkflowGlobalConfigurationDrawer(false);
                  setOpenWorkflowStorageDrawer(false);
                  setOpenWorkflowManualRunDrawer(false);
                  setOpenWorkflowPauseDrawer(false);
                  setOpenWorkflowResumeDrawer(true);
                  setOpenWorkflowSkipDrawer(false);
                  setOpenWorkflowCancelDrawer(false);
                }}
              >
                <PlaySquare className="mr-1 h-4 w-4" /> Resume Run
              </div>
              <Separator orientation="vertical" />
              <div
                className={`flex items-center rounded-md p-1 pl-2 pr-2  ${
                  !isWorkflowPublished
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:cursor-pointer hover:bg-slate-200'
                }`}
                onClick={() => {
                  if (!isWorkflowPublished) return;
                  setCurrentlyClickedNode(null);
                  setOpenNodeConfigurationDrawer(false);
                  setOpenWorkflowGlobalConfigurationDrawer(false);
                  setOpenWorkflowStorageDrawer(false);
                  setOpenWorkflowManualRunDrawer(false);
                  setOpenWorkflowPauseDrawer(true);
                  setOpenWorkflowResumeDrawer(false);
                  setOpenWorkflowSkipDrawer(false);
                  setOpenWorkflowCancelDrawer(false);
                }}
              >
                <PauseCircle className="mr-1 h-4 w-4" /> Pause Run
              </div>
              <Separator orientation="vertical" />
              <div
                className={`flex items-center rounded-md p-1 pl-2 pr-2  ${
                  !isWorkflowPublished
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:cursor-pointer hover:bg-slate-200'
                }`}
                onClick={() => {
                  if (!isWorkflowPublished) return;
                  setCurrentlyClickedNode(null);
                  setOpenNodeConfigurationDrawer(false);
                  setOpenWorkflowGlobalConfigurationDrawer(false);
                  setOpenWorkflowStorageDrawer(false);
                  setOpenWorkflowManualRunDrawer(true);
                  setOpenWorkflowPauseDrawer(false);
                  setOpenWorkflowResumeDrawer(false);
                  setOpenWorkflowSkipDrawer(false);
                  setOpenWorkflowCancelDrawer(false);
                }}
              >
                <PlayCircle className="mr-1 h-4 w-4" /> Manual Run
              </div>
              {workflow?.data?.type !== 'BulkRun' && (
                <>
                  <Separator orientation="vertical" />
                  <div
                    className={`flex items-center rounded-md p-1 pl-2 pr-2  ${
                      !isWorkflowPublished
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:cursor-pointer hover:bg-slate-200'
                    }`}
                    onClick={() => {
                      if (!isWorkflowPublished) return;
                      dryRunMutation.mutateAsync(workflowId);
                    }}
                  >
                    <Play className="mr-1 h-4 w-4" />
                    {dryRunMutation.isPending ? 'Running...' : 'Test Run'}
                  </div>
                </>
              )}
              {workflow?.data?.type !== 'BulkRun' && (
                <>
                  <Separator orientation="vertical" />
                  <div
                    className={`flex items-center rounded-md p-1 pl-2 pr-2  ${
                      isWorkflowPublished
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:cursor-pointer hover:bg-slate-200'
                    }`}
                    onClick={() => {
                      if (isWorkflowPublished) return;
                      setCurrentlyClickedNode(null);
                      setOpenNodeConfigurationDrawer(false);
                      setOpenWorkflowStorageDrawer(false);
                      setOpenWorkflowGlobalConfigurationDrawer(true);
                      setOpenWorkflowManualRunDrawer(false);
                      setOpenWorkflowPauseDrawer(false);
                      setOpenWorkflowResumeDrawer(false);
                      setOpenWorkflowSkipDrawer(false);
                      setOpenWorkflowCancelDrawer(false);
                    }}
                  >
                    <Cog className="mr-1 h-4 w-4" /> Settings
                  </div>
                </>
              )}

              {/* <Separator orientation="vertical" />
              <div
                className={`flex items-center rounded-md p-1 pl-2 pr-2  ${isWorkflowPublished
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:cursor-pointer hover:bg-slate-200'
                  }`} onClick={() => {
                    if (isWorkflowPublished) return;
                    setCurrentlyClickedNode(null);
                    setOpenNodeConfigurationDrawer(false);
                    setOpenWorkflowGlobalConfigurationDrawer(false);
                    setOpenWorkflowStorageDrawer(true);
                    setOpenWorkflowManualRunDrawer(false);
                    setOpenWorkflowPauseDrawer(false);
                    setOpenWorkflowResumeDrawer(false);
                    setOpenWorkflowSkipDrawer(false);
                  }}
              >
                <PackageOpen className="mr-1 h-4 w-4" /> Storage
              </div> */}
            </div>

            {/* {workflow?.data?.type !== 'BulkRun' && ( */}
            <>
              {workflow?.data?.isActive && (
                <Button
                  size="sm"
                  disabled={
                    updateWorkflowMutation.isPending ||
                    unpublishBulkMutation.isPending
                  }
                  onClick={async () => {
                    if (workflow?.data?.type === 'BulkRun') {
                      // If it's a bulk workflow, call the bulk unpublish mutation.
                      await unpublishBulkMutation.mutateAsync(workflowId);
                    } else {
                      // Otherwise, use the regular update mutation.
                      await updateWorkflowMutation.mutateAsync({
                        workflowId,
                        payload: { isActive: false },
                      });
                    }
                  }}
                >
                  Unpublish
                </Button>
              )}

              {!workflow?.data?.isActive && (
                <Button
                  size="sm"
                  disabled={updateWorkflowMutation.isPending}
                  onClick={async () => {
                    // Filter Timer nodes from the workflow nodes.
                    const timerNodes = nodes.filter(
                      (node: any) => node.type === 'TIMER',
                    );

                    // Find any Timer node with an invalid delay value (missing or zero)
                    const invalidTimerNodes = timerNodes.filter((node: any) => {
                      const delayAmount = node.data.delayAmount;
                      // Check if delayAmount is undefined, null, or zero
                      return (
                        delayAmount === undefined ||
                        delayAmount === null ||
                        delayAmount === 0
                      );
                    });

                    if (invalidTimerNodes.length > 0) {
                      toast.error(
                        'Please set timer values (delay amount and unit) for all Timer nodes before publishing.',
                        { duration: 5000 },
                      );
                      return;
                    }

                    await publishWorkflowMutation.mutateAsync(workflowId);

                    // await updateWorkflowMutation.mutateAsync({
                    //   workflowId,
                    //   payload: {
                    //     isActive: true,
                    //     executionFrequencyType:
                    //       workflow?.data?.executionFrequencyType,
                    //     timeZone: workflow?.data?.timeZone,
                    //     executionHour: workflow?.data?.executionHour,
                    //     executionMinute: workflow?.data?.executionMinute,
                    //     executionDaysOfWeek:
                    //       workflow?.data?.executionDaysOfWeek,
                    //     executionDaysOfMonth:
                    //       workflow?.data?.executionDaysOfMonth,
                    //   },
                    // });
                  }}
                >
                  Publish
                </Button>
              )}
            </>
            {/* )} */}
          </div>
        </div>

        <ReactFlow
          nodes={nodes}
          onNodesChange={isWorkflowPublished ? undefined : onNodesChange}
          edges={edges}
          onEdgesChange={isWorkflowPublished ? undefined : onEdgesChange}
          onPaneClick={onPaneClick}
          // fitView
          defaultViewport={{
            zoom: 0.9,
            x: 600,
            y: 200,
          }}
          onConnect={isWorkflowPublished ? undefined : onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodeExtent={[
            [-Infinity, -Infinity],
            [Infinity, Infinity],
          ]} // Restrict node movement
          onNodeClick={(event, node) => {
            onNodeClick(event, node); // ✅ Open drawer regardless of published state
            setCurrentlyClickedNode(node);
          }}
          // nodesDraggable={false}
        >
          <Background bgColor="#f0f2f7" />
          <Controls />

          <NodeConfigurationDrawer
            open={!!$selectedNode.get()}
            setOpen={setOpenNodeConfigurationDrawer}
            nodeType={currentlyClickedNode?.type || null}
            setNodes={setNodes}
            selectedNode={currentlyClickedNode}
            workflowId={workflowId}
            setSelectedNode={setCurrentlyClickedNode}
            readOnly={false}
          />

          <WorkflowGlobalConfigurationDrawer
            open={openWorkflowGlobalConfigurationDrawer}
            setOpen={setOpenWorkflowGlobalConfigurationDrawer}
            setNodes={setNodes}
            selectedNode={currentlyClickedNode}
          />

          <WorkflowStorage
            open={openWorkflowStorageDrawer}
            setOpen={setOpenWorkflowStorageDrawer}
            setNodes={setNodes}
            selectedNode={currentlyClickedNode}
            workFlowId={workflowId}
          />

          <WorkflowManualRunDrawer
            open={openWorkflowManualRunDrawer}
            setOpen={setOpenWorkflowManualRunDrawer}
            setNodes={setNodes}
            selectedNode={currentlyClickedNode}
            workFlowId={workflowId}
          />

          <WorkflowPauseDrawer
            open={openWorkflowPauseDrawer}
            setOpen={setOpenWorkflowPauseDrawer}
            setNodes={setNodes}
            selectedNode={currentlyClickedNode}
            workFlowId={workflowId}
          />

          <WorkflowResumeDrawer
            open={openWorkflowResumeDrawer}
            setOpen={setOpenWorkflowResumeDrawer}
            setNodes={setNodes}
            selectedNode={currentlyClickedNode}
            workFlowId={workflowId}
          />

          <WorkflowSkipDrawer
            open={openWorkflowSkipDrawer}
            setOpen={setOpenWorkflowSkipDrawer}
            setNodes={setNodes}
            selectedNode={currentlyClickedNode}
            workFlowId={workflowId}
          />

          <WorkflowCancelDrawer
            open={openWorkflowCancelDrawer}
            setOpen={setOpenWorkflowCancelDrawer}
            setNodes={setNodes}
            selectedNode={currentlyClickedNode}
            workFlowId={workflowId}
          />

          {addingNode && (
            <div className="absolute left-[50%] top-[40%] z-50 flex animate-bounce items-center justify-center rounded-xl bg-slate-200 p-2 pb-1 pt-1 text-center text-sm text-muted-foreground">
              Adding node
              <Loader2 className="ml-2 h-4 w-4 animate-spin text-primary" />
            </div>
          )}

          {deletingNode && (
            <div className="absolute left-[50%] top-[40%] z-50 flex animate-bounce items-center justify-center rounded-xl bg-slate-200 p-2 pb-1 pt-1 text-center text-sm text-muted-foreground">
              Removing node
              <Loader2 className="ml-2 h-4 w-4 animate-spin text-primary" />
            </div>
          )}

          {addEdgeMutation?.isPending && (
            <div className="absolute left-[50%] top-[40%] z-50 flex animate-bounce items-center justify-center rounded-xl bg-slate-200 p-2 pb-1 pt-1 text-center text-sm text-muted-foreground">
              Adding connection
              <Loader2 className="ml-2 h-4 w-4 animate-spin text-primary" />
            </div>
          )}

          {deletingEdge && (
            <div className="absolute left-[50%] top-[40%] z-50 flex animate-bounce items-center justify-center rounded-xl bg-slate-200 p-2 pb-1 pt-1 text-center text-sm text-muted-foreground">
              Removing connection
              <Loader2 className="ml-2 h-4 w-4 animate-spin text-primary" />
            </div>
          )}
        </ReactFlow>
      </div>
    </div>
  );
};

export const WorkflowBuilder = ({ workflowId }: CreateFlowPageProps) => {
  return (
    <ReactFlowProvider>
      <Workflow workflowId={workflowId} />
    </ReactFlowProvider>
  );
};
